import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../contexts/ToastContext';
import { COLORS, FONTS } from '../../styles/tokens';
import { ConfirmationModal } from '../../components/modal/ConfirmationModal';
import { InfoCard, InfoRow, StatusBadge } from '../../components/app/DeployedAppInfoCard';
import { DeployedAppMetaEditor } from '../../components/app/DeployedAppMetaEditor';
import { useDeployedAppMetaEdit } from '../../hooks/useDeployedAppMetaEdit';

interface DeployedApp {
  id: string;
  slug: string;
  name: string;
  description: string;
  owner_email: string;
  current_version: number;
  image_tag: string;
  build_status: string;
  build_log: string;
  deploy_status: string;
  app_port: number;
  app_url: string;
  team: string;
  category: string;
  icon_url: string;
  cpu_limit: string;
  memory_limit: string;
  created_at: string;
  updated_at: string;
  last_healthy_at: string;
}

/** Deployed app detail/control panel */
export default function DeployedAppDetail(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const [app, setApp] = useState<DeployedApp | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [showLogs, setShowLogs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toast = useToast();

  const loadApp = useCallback(async () => {
    try {
      const res = await fetch(`/api/apps/deployed/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('App not found');
      setApp(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load app');
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => { loadApp(); }, [loadApp]);

  const doAction = useCallback(async (action: string, method = 'POST') => {
    if (!app) return;
    setActionLoading(action);
    try {
      const url = action === 'delete'
        ? `/api/apps/deployed/${app.slug}`
        : `/api/apps/deployed/${app.slug}/${action}`;
      const res = await fetch(url, {
        method: action === 'delete' ? 'DELETE' : method,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Action failed: ${res.status}`);
      if (action === 'delete') {
        toast.push({ variant: 'success', title: 'App deleted', body: `${app.name} removed`, timeoutMs: 5000 });
        navigate('/apps');
      } else {
        toast.push({ variant: 'info', title: `App ${action}`, body: `${app.name}`, timeoutMs: 3000 });
        await loadApp();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading('');
    }
  }, [app, token, navigate, loadApp]);

  const meta = useDeployedAppMetaEdit({
    app, token, reload: loadApp,
    onSaved: slug => toast.push({ variant: 'success', title: 'App updated', body: slug, timeoutMs: 3000 }),
    onError: setError,
  });

  const loadLogs = useCallback(async () => {
    if (!app) return;
    try {
      const res = await fetch(`/api/apps/deployed/${app.slug}/logs?lines=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setShowLogs(true);
      }
    } catch {
      setError('Failed to load logs');
    }
  }, [app, token]);

  if (loading) {
    return <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading...</p>;
  }
  if (!app) {
    return <p style={{ fontFamily: FONTS.body, color: '#EF4444' }}>{error ?? 'App not found'}</p>;
  }

  const blueBtnStyle: React.CSSProperties = {
    height: '32px', padding: '0 14px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFF', fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em',
    textTransform: 'uppercase', cursor: 'pointer',
  };

  const outlineBtnStyle: React.CSSProperties = {
    height: '32px', padding: '0 14px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, background: 'transparent',
    color: colors.textSecondary, fontFamily: FONTS.heading, fontSize: '10px',
    letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer',
  };

  const dangerBtnStyle: React.CSSProperties = {
    ...outlineBtnStyle, borderColor: '#EF4444', color: '#EF4444',
  };

  return (
    <div>
      {/* Header */}
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <div>
          <button onClick={() => navigate('/apps')} style={{ ...outlineBtnStyle, marginBottom: '8px', fontSize: '9px' }}>
            Back to Apps
          </button>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
            {app.name}
          </h2>
          <p style={{ fontFamily: 'monospace', fontSize: '12px', color: colors.textSecondary, margin: '4px 0 0' }}>
            {app.slug}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {app.deploy_status === 'running' && (
            <button onClick={() => doAction('stop')} disabled={!!actionLoading} style={outlineBtnStyle}>
              {actionLoading === 'stop' ? '...' : 'Stop'}
            </button>
          )}
          {app.deploy_status === 'stopped' && (
            <button onClick={() => doAction('start')} disabled={!!actionLoading} style={blueBtnStyle}>
              {actionLoading === 'start' ? '...' : 'Start'}
            </button>
          )}
          <button onClick={meta.editing ? meta.cancel : meta.start} style={outlineBtnStyle}>
            {meta.editing ? 'Close Edit' : 'Edit'}
          </button>
          <button onClick={() => navigate(`/apps/deploy?slug=${app.slug}`)} style={blueBtnStyle}>
            Chat
          </button>
          <button onClick={() => setShowDeleteModal(true)} disabled={!!actionLoading} style={dangerBtnStyle}>
            {actionLoading === 'delete' ? '...' : 'Delete'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '12px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {meta.editing && (
        <DeployedAppMetaEditor
          appName={app.name}
          iconUrl={app.icon_url ?? ''}
          name={meta.name} description={meta.description} category={meta.category} team={meta.team}
          icon={meta.icon}
          onNameChange={meta.setName} onDescriptionChange={meta.setDescription}
          onCategoryChange={meta.setCategory} onTeamChange={meta.setTeam} onIconChange={meta.setIcon}
          onSave={meta.save} onCancel={meta.cancel}
          saving={meta.saving} isDark={isDark} colors={colors}
          blueBtnStyle={blueBtnStyle} outlineBtnStyle={outlineBtnStyle}
        />
      )}

      {/* Status cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <InfoCard title="Build" isDark={isDark} colors={colors}>
          <StatusBadge status={app.build_status} />
          <InfoRow label="Version" value={`v${app.current_version}`} colors={colors} />
          <InfoRow label="Image" value={app.image_tag ?? 'N/A'} colors={colors} mono />
          {app.build_log && (
            <button onClick={() => setShowLogs(!showLogs)} style={{ ...outlineBtnStyle, height: '28px', fontSize: '9px', marginTop: '4px' }}>
              {showLogs ? 'Hide Build Log' : 'Show Build Log'}
            </button>
          )}
        </InfoCard>

        <InfoCard title="Deployment" isDark={isDark} colors={colors}>
          <StatusBadge status={app.deploy_status} />
          <InfoRow label="URL" value={app.app_url ?? 'N/A'} colors={colors} mono />
          <InfoRow label="Port" value={String(app.app_port)} colors={colors} />
          <InfoRow label="CPU" value={app.cpu_limit} colors={colors} />
          <InfoRow label="Memory" value={app.memory_limit} colors={colors} />
          <button onClick={loadLogs} style={{ ...outlineBtnStyle, height: '28px', fontSize: '9px', marginTop: '4px' }}>
            View Pod Logs
          </button>
        </InfoCard>

        <InfoCard title="Details" isDark={isDark} colors={colors}>
          <InfoRow label="Owner" value={app.owner_email} colors={colors} />
          <InfoRow label="Team" value={app.team || 'N/A'} colors={colors} />
          <InfoRow label="Category" value={app.category} colors={colors} />
          <InfoRow label="Created" value={new Date(app.created_at).toLocaleDateString()} colors={colors} />
          {app.last_healthy_at && (
            <InfoRow label="Last Healthy" value={new Date(app.last_healthy_at).toLocaleString()} colors={colors} />
          )}
        </InfoCard>
      </div>

      {/* Log viewer */}
      {showLogs && (
        <div style={{
          padding: '14px', borderRadius: '10px', border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? '#0d0d0d' : '#f8f8f8',
          maxHeight: '400px', overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em', color: colors.textSecondary, textTransform: 'uppercase' }}>
              Logs
            </span>
            <button onClick={() => setShowLogs(false)} style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: '12px' }}>
              Close
            </button>
          </div>
          <pre style={{ fontFamily: 'monospace', fontSize: '11px', color: colors.text, whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.6' }}>
            {logs || app.build_log || 'No logs available'}
          </pre>
        </div>
      )}

      <ConfirmationModal
        open={showDeleteModal}
        variant="danger"
        title="Delete this app?"
        body={
          <>
            This tears down the k8s resources, Keycloak client, Keycloak theme,
            MinIO artifacts, and Secret Manager secrets for <code>{app.slug}</code>.
            This cannot be undone.
          </>
        }
        requiredText={app.slug}
        confirmLabel={`Delete ${app.name}`}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          setShowDeleteModal(false);
          await doAction('delete');
        }}
      />
    </div>
  );
}

